import React, { useState, useEffect, useRef, useCallback } from "react";
import { Table } from "react-bootstrap";
import CreatableSelect from "react-select/creatable";
import { formatDate, renderCustomPagination } from "./candidateHelpers";
import CandidateTableBody from "./CandidateTableBody";

const ListTab = ({
    folderOptions,
    folderLoading,
    selectedFolder,
    onFolderChange,
    listOptions,
    listLoading,
    selectedListView,
    listViewCandidates,
    listViewLoading,
    listViewPagination,
    onListViewPageChange,
    onListSelect,
    onViewSummary,
    onDownload,
    onAddClick,
    getListsByFolder,
}) => {
    const [searchText, setSearchText] = useState("");
    const [searchDisabled, setSearchDisabled] = useState(false);
    const debounceRef = useRef(null);

    // Disable search bar as soon as folder filter triggers a load, re-enable when done
    useEffect(() => {
        if (listLoading) {
            setSearchDisabled(true);
        } else {
            setSearchDisabled(false);
        }
    }, [listLoading]);

    // Clear search text when folder changes
    useEffect(() => {
        setSearchText("");
    }, [selectedFolder]);

    const handleSearchChange = useCallback(
        (e) => {
            const value = e.target.value;
            setSearchText(value);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                if (getListsByFolder) {
                    getListsByFolder({ name: value, folder_id: selectedFolder });
                }
            }, 500);
        },
        [selectedFolder, getListsByFolder]
    );
    const [listPage, setListPage] = useState(1);
    const [listPageSize, setListPageSize] = useState(10);
    const [candPageSize, setCandPageSize] = useState(10);
    const [candSearchText, setCandSearchText] = useState("");
    const candDebounceRef = useRef(null);
    useEffect(() => { setListPage(1); }, [selectedFolder]);
    useEffect(() => { setCandPageSize(10); setCandSearchText(""); }, [selectedListView]);

    const handleCandSearchChange = useCallback(
        (e) => {
            const value = e.target.value;
            setCandSearchText(value);
            if (candDebounceRef.current) clearTimeout(candDebounceRef.current);
            candDebounceRef.current = setTimeout(() => {
                if (onListViewPageChange) onListViewPageChange({ limit: candPageSize, page: 1, search: value });
            }, 500);
        },
        [candPageSize, onListViewPageChange]
    );

    if (selectedListView) {
        const total = listViewPagination?.total || 0;
        const currentPage = listViewPagination?.page || 1;
        const totalCandPages = Math.ceil(total / candPageSize);
        const candStart = total > 0 ? (currentPage - 1) * candPageSize + 1 : 0;
        const candEnd = Math.min(currentPage * candPageSize, total);

        return (
            <div className="table-content">
                <div className="controls">
                    <div className="has-search">
                        <span className="form-control-feedback">
                            <img src="/images/search.svg" alt="" />
                        </span>
                        <input
                            type="text"
                            className="search-bar"
                            placeholder="Search"
                            value={candSearchText}
                            onChange={handleCandSearchChange}
                        />
                    </div>
                </div>

                <Table responsive className="job-table">
                    <thead>
                        <tr>
                            <th style={{ paddingLeft: '30px' }}>Name</th>
                            <th className="text-center">Email</th>
                            <th className="text-center">Designation</th>
                            <th className="text-center">Added On</th>
                            <th>Skills</th>
                            <th className="text-center">List Name</th>
                            <th className="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {listViewLoading ? (
                            <tr>
                                <td colSpan="6">
                                    <div className="loading-overlay" style={{ alignItems: 'start', color: '#F75891', height: 'auto' }}>
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading candidates...</span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            <CandidateTableBody
                                candidates={listViewCandidates}
                                onViewSummary={onViewSummary}
                                onDownload={onDownload}
                                onAddClick={onAddClick}
                            />
                        )}
                    </tbody>
                </Table>

                {!listViewLoading && total > 0 && (
                    <div className="pagination d-flex flex-wrap align-items-center justify-content-between">
                        <div className="d-flex flex-wrap" style={{ gap: "40px" }}>
                            <div className="d-flex flex-wrap" style={{ gap: "20px" }}>
                                <div className="pagination-info d-flex align-items-center">
                                    <span style={{ color: "#525B66" }}>
                                        Showing {candStart}–{candEnd} of{" "}
                                        <strong>{total}</strong> Candidates
                                    </span>
                                </div>
                                <div className="pagination-buttons">
                                    {renderCustomPagination(totalCandPages, currentPage, (p) => {
                                        if (onListViewPageChange) onListViewPageChange({ limit: candPageSize, page: p, search: candSearchText });
                                    })}
                                </div>
                            </div>
                            <div className="d-flex align-items-center">
                                <span style={{ color: '#525B66', lineHeight: '100%' }}>Candidates per page</span>&nbsp;&nbsp;&nbsp;&nbsp;
                                <select
                                    value={candPageSize}
                                    onChange={(e) => {
                                        const newSize = parseInt(e.target.value);
                                        setCandPageSize(newSize);
                                        if (onListViewPageChange) onListViewPageChange({ limit: newSize, page: 1, search: candSearchText });
                                    }}
                                    style={{ width: "46px", height: '32px', borderRadius: '2px', border: '1px solid #8A9199', color: '#8A9199' }}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    const totalListPages = Math.ceil((listOptions.length || 0) / listPageSize);
    const pagedLists = listOptions.slice((listPage - 1) * listPageSize, listPage * listPageSize);
    const listStart = (listPage - 1) * listPageSize + 1;
    const listEnd = Math.min(listPage * listPageSize, listOptions.length);

    const selectStyles = {
        control: (base) => ({ ...base, width: 167, minWidth: 167, borderRadius: "4px", color: "#1f2a37", fontSize: "14px", lineHeight: "18px", fontFamily: '"Inter", sans-serif' }),
        menu: (base) => ({ ...base, minWidth: 167, fontFamily: '"Inter", sans-serif' }),
        menuList: (base) => ({ ...base, overflowX: "hidden" }),
        placeholder: (base) => ({ ...base, color: "#1f2a37", fontFamily: '"Inter", sans-serif' }),
        option: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
        singleValue: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
        input: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
    };

    return (
        <div className="table-content">
            <div className="controls">
                <div className="has-search">
                    <span className="form-control-feedback">
                        <img src="/images/search.svg" alt="" />
                    </span>
                    <input
                        type="text"
                        className="search-bar"
                        placeholder="Search"
                        value={searchText}
                        disabled={searchDisabled}
                        onChange={handleSearchChange}
                        style={searchDisabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                    />
                </div>
                <div className="filter-buttons">
                    <CreatableSelect
                        className="select-btn"
                        inputId="folder-filter"
                        value={folderOptions.find((f) => f.value === selectedFolder) || null}
                        onChange={(option) => onFolderChange(option?.value ?? "")}
                        options={folderOptions}
                        isLoading={folderLoading}
                        isDisabled={folderLoading}
                        isClearable
                        placeholder="All Folders"
                        maxMenuHeight={200}
                        classNamePrefix="rs-filter"
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={selectStyles}
                    />
                </div>
            </div>

            <Table responsive className="job-table">
                <thead>
                    <tr>
                        <th style={{ paddingLeft: '30px' }}>Lists</th>
                        <th className="text-center">Folder</th>
                        <th className="text-center">Candidates</th>
                        <th className="text-center">Created On</th>
                    </tr>
                </thead>
                <tbody>
                    {listLoading ? (
                        <tr>
                            <td colSpan="4">
                                <div className="loading-overlay" style={{ alignItems: 'start', color: '#F75891', height: 'auto' }}>
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading lists...</span>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    ) : listOptions.length === 0 ? (
                        <tr>
                            <td
                                colSpan={4}
                                style={{ textAlign: "center", color: "#C3C3C3", fontSize: "14px" }}
                            >
                                {selectedFolder
                                    ? (
                                        <div>
                                            No lists yet. Click{" "}
                                            <strong
                                                style={{ cursor: "pointer", color: "#F75891" }}
                                                onClick={onAddClick}
                                            >
                                                Add Candidates
                                            </strong>{" "}
                                            to get started.
                                        </div>
                                    )
                                    : "Please select a folder to get started."}
                            </td>
                        </tr>
                    ) : (
                        pagedLists.map((c) => (
                            <tr key={c.id}>
                                <td style={{ paddingLeft: '30px' }}>
                                    <div className="d-flex flex-column gap-1">
                                        <strong
                                            style={{ cursor: "pointer" }}
                                            onClick={() => onListSelect(c)}
                                            onMouseEnter={(e) => e.target.style.color = "#F75891"}
                                            onMouseLeave={(e) => e.target.style.color = "#313539"}
                                        >
                                            {c.name}
                                        </strong>
                                    </div>
                                </td>
                                <td className="text-center">
                                    {c.folder?.name || <span style={{ color: "#C3C3C3" }}>N/A</span>}
                                </td>
                                <td className="text-center">{c.candidates_count || 0}</td>
                                <td className="text-center">{formatDate(c.created_at)}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>

            {!listLoading && listOptions.length > 0 && (
                <div className="pagination d-flex flex-wrap align-items-center justify-content-between">
                    <div className="d-flex flex-wrap" style={{ gap: "40px" }}>
                        <div className="d-flex flex-wrap" style={{ gap: "20px" }}>
                            <div className="pagination-info d-flex align-items-center">
                                <span style={{ color: "#525B66" }}>
                                    Showing {listStart}–{listEnd} of{" "}
                                    <strong>{listOptions.length}</strong> Lists
                                </span>
                            </div>
                            <div className="pagination-buttons">
                                {renderCustomPagination(totalListPages, listPage, setListPage)}
                            </div>
                        </div>
                        <div className="d-flex align-items-center">
                            <span style={{ color: '#525B66', lineHeight: '100%' }}>Lists per page</span>&nbsp;&nbsp;&nbsp;&nbsp;
                            <select
                                value={listPageSize}
                                onChange={(e) => {
                                    setListPageSize(parseInt(e.target.value));
                                    setListPage(1);
                                }}
                                style={{ width: "46px", height: '32px', borderRadius: '2px', border: '1px solid #8A9199', color: '#8A9199' }}
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ListTab;
